import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Separator } from '../components/ui/separator';
import {
  Plus, Search, UtensilsCrossed, MapPin, Mail, Sparkles,
  DollarSign, MoreHorizontal, Edit, Trash2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import api from '../services/api';
import { toast } from 'sonner';

const VILLAGES = [
  'All', 'Southampton', 'East Hampton', 'Bridgehampton', 'Montauk',
  'Sag Harbor', 'Amagansett', 'Wainscott', 'Water Mill',
  'Quogue', 'Hampton Bays', 'Shelter Island', 'Other'
];

const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'N/A';

const EMPTY_FORM = { name: '', village: '', address: '', cuisine: '', owner_name: '', phone: '', website: '', seat_count: '', avg_check: '', turns_per_night: '', notes: '' };

export default function Restaurants() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [village, setVillage] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);

  const loadRestaurants = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (village !== 'All') params.village = village;
      if (search) params.search = search;
      const res = await api.get('/api/restaurants', { params });
      setRestaurants(res.data.restaurants || []);
    } catch { toast.error('Failed to load restaurants'); }
    finally { setLoading(false); }
  }, [village, search]);

  useEffect(() => {
    const timer = setTimeout(loadRestaurants, 300);
    return () => clearTimeout(timer);
  }, [loadRestaurants]);

  const openAdd = () => { setEditItem(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (r) => {
    setEditItem(r);
    setForm({
      name: r.name || '', village: r.village || '', address: r.address || '',
      cuisine: r.cuisine || '', owner_name: r.owner_name || '', phone: r.phone || '',
      website: r.website || '', seat_count: r.seat_count || '', avg_check: r.avg_check || '',
      turns_per_night: r.turns_per_night || '', notes: r.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Restaurant name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        seat_count: form.seat_count ? parseInt(form.seat_count) : null,
        avg_check: form.avg_check ? parseFloat(form.avg_check) : null,
        turns_per_night: form.turns_per_night ? parseFloat(form.turns_per_night) : null,
      };
      if (editItem) {
        await api.put(`/api/restaurants/${editItem.id}`, payload);
        toast.success('Restaurant updated');
      } else {
        await api.post('/api/restaurants', payload);
        toast.success('Restaurant added');
      }
      setShowModal(false);
      loadRestaurants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this restaurant?')) return;
    try {
      await api.delete(`/api/restaurants/${id}`);
      toast.success('Restaurant deleted');
      loadRestaurants();
      if (selectedRestaurant?.id === id) setSelectedRestaurant(null);
    } catch { toast.error('Failed to delete'); }
  };

  const getAiAssessment = async (r) => {
    setSelectedRestaurant(r);
    setAiAssessment(null);
    setLoadingAi(true);
    try {
      const res = await api.post(`/api/ai/revenue-health/${r.id}`);
      setAiAssessment(res.data.assessment);
    } catch { toast.error('Failed to get assessment'); }
    finally { setLoadingAi(false); }
  };

  const healthClass = (h) => ({ Strong: 'badge-strong', Moderate: 'badge-moderate', Weak: 'badge-weak', Critical: 'badge-critical' }[h] || '');

  return (
    <div className="space-y-5">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Restaurants</h1>
          <p className="page-subtitle">{restaurants.length} restaurants tracked with revenue intelligence</p>
        </div>
        <Button onClick={openAdd} className="bg-primary" data-testid="restaurants-add-button">
          <Plus size={16} className="mr-1.5" /> Add Restaurant
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search restaurants..." className="pl-9" />
        </div>
        <Select value={village} onValueChange={setVillage}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All villages" />
          </SelectTrigger>
          <SelectContent>
            {VILLAGES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : restaurants.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <UtensilsCrossed size={40} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No restaurants found.</p>
          <Button onClick={openAdd} variant="outline" size="sm" className="mt-3">Add first restaurant</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {restaurants.map(r => (
            <Card key={r.id} className="hover:shadow-md transition-shadow" data-testid={`restaurant-card-${r.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{r.name}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {r.village && <Badge variant="secondary" className="text-xs"><MapPin size={10} className="mr-1" />{r.village}</Badge>}
                      {r.cuisine && <span className="text-xs text-muted-foreground">{r.cuisine}</span>}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="w-8 h-8 flex-shrink-0">
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(r)}><Edit size={14} className="mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => getAiAssessment(r)}><Sparkles size={14} className="mr-2" />AI Assessment</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(r.id)} className="text-destructive">
                        <Trash2 size={14} className="mr-2" />Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {r.daily_revenue ? (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="revenue-metric">
                      <div className="value text-sm">{fmt(r.daily_revenue)}</div>
                      <div className="label">Daily</div>
                    </div>
                    <div className="revenue-metric">
                      <div className="value text-sm">{fmt(r.weekly_peak)}</div>
                      <div className="label">Wk Peak</div>
                    </div>
                    <div className="revenue-metric">
                      <div className="value text-sm">{fmt(r.monthly_seasonal)}</div>
                      <div className="label">Monthly</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-2 mb-3 text-center">
                    Add seat count, avg check & turns to see revenue
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground">
                    {r.seat_count && <span>{r.seat_count} seats</span>}
                    {r.avg_check && <span> · ${r.avg_check} avg</span>}
                    {r.turns_per_night && <span> · {r.turns_per_night}x turns</span>}
                  </div>
                  {r.financial_health && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${healthClass(r.financial_health)}`}>
                      {r.financial_health}
                    </span>
                  )}
                </div>
                {r.owner_name && (
                  <div className="text-xs text-muted-foreground mt-1.5">• Owner: {r.owner_name}</div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {editItem ? 'Edit Restaurant' : 'Add Restaurant'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2">
              <Label>Restaurant Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="The Barn at Mecox" data-testid="restaurant-form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Village</Label>
              <Select value={form.village} onValueChange={v => setForm(p => ({ ...p, village: v }))}>
                <SelectTrigger><SelectValue placeholder="Select village" /></SelectTrigger>
                <SelectContent>{VILLAGES.slice(1).map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Cuisine Type</Label>
              <Input value={form.cuisine} onChange={e => setForm(p => ({ ...p, cuisine: e.target.value }))} placeholder="American, Italian, Seafood..." />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St, Southampton, NY" />
            </div>
            <div className="space-y-1.5">
              <Label>Owner Name</Label>
              <Input value={form.owner_name} onChange={e => setForm(p => ({ ...p, owner_name: e.target.value }))} placeholder="John Smith" />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 631 555 0100" />
            </div>
            <Separator className="col-span-2" />
            <div className="col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Revenue Intelligence</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Seat Count</Label>
                  <Input type="number" value={form.seat_count} onChange={e => setForm(p => ({ ...p, seat_count: e.target.value }))} placeholder="80" />
                </div>
                <div className="space-y-1.5">
                  <Label>Avg Check ($)</Label>
                  <Input type="number" value={form.avg_check} onChange={e => setForm(p => ({ ...p, avg_check: e.target.value }))} placeholder="75" />
                </div>
                <div className="space-y-1.5">
                  <Label>Turns/Night</Label>
                  <Input type="number" step="0.1" value={form.turns_per_night} onChange={e => setForm(p => ({ ...p, turns_per_night: e.target.value }))} placeholder="2.5" />
                </div>
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} placeholder="Observations, context, intel..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="restaurant-save-button">
              {saving ? 'Saving...' : editItem ? 'Save Changes' : 'Add Restaurant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Assessment Sheet */}
      <Sheet open={!!selectedRestaurant} onOpenChange={(o) => { if (!o) { setSelectedRestaurant(null); setAiAssessment(null); } }}>
        <SheetContent side="right" className="w-[420px]">
          <SheetHeader className="mb-4">
            <SheetTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {selectedRestaurant?.name}
            </SheetTitle>
            <Badge variant="secondary">{selectedRestaurant?.village}</Badge>
          </SheetHeader>
          {selectedRestaurant && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="revenue-metric"><div className="value">{fmt(selectedRestaurant.daily_revenue)}</div><div className="label">Daily Revenue</div></div>
                <div className="revenue-metric"><div className="value">{fmt(selectedRestaurant.weekly_peak)}</div><div className="label">Weekly Peak</div></div>
                <div className="revenue-metric"><div className="value">{fmt(selectedRestaurant.monthly_seasonal)}</div><div className="label">Monthly Seasonal</div></div>
                <div className="revenue-metric"><div className="value">{fmt(selectedRestaurant.contribution_margin)}</div><div className="label">Contribution Margin</div></div>
              </div>
              {selectedRestaurant.financial_health && (
                <div className={`text-center py-2 rounded-lg font-medium text-sm badge-${selectedRestaurant.financial_health?.toLowerCase()}`}>
                  Financial Health: {selectedRestaurant.financial_health}
                </div>
              )}
              <Separator />
              <div>
                <p className="text-sm font-semibold mb-2">AI Revenue Assessment</p>
                {loadingAi ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </div>
                ) : aiAssessment ? (
                  <div className="text-sm text-foreground bg-accent/20 rounded-lg p-3 prose-content">
                    {aiAssessment}
                  </div>
                ) : (
                  <Button size="sm" onClick={() => getAiAssessment(selectedRestaurant)} className="w-full" data-testid="revenue-ai-assess-button">
                    <Sparkles size={14} className="mr-1.5" /> Generate AI Assessment
                  </Button>
                )}
              </div>
              {selectedRestaurant.notes && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground">{selectedRestaurant.notes}</p>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
